import { MenuItem, Text } from "@chakra-ui/react";
import { Glossary } from "src/components/Text/Glossary";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { maybePluralize } from "src/services/format";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canRemoveFromWaitlists } from "../validations/removeFromWaitlists";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction } from "./trackFormAction";
import { useEnqueueBulkWaitlist } from "./useEnqueueBulkWaitlist";

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
  menuLabel?: string;
}

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "You will remove this form from its school and grade waitlist.",
    "You will remove these forms from each of the selected school and grades waitlists."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(
    count,
    "Yes, remove from waitlist",
    "Yes, remove from waitlists"
  );

export const BulkRemoveFromWaitlists = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
  menuLabel = "Remove from waitlists",
}: Props) => {
  const bulk = useBulkActionTargets();
  const enqueueBulkWaitlist = useEnqueueBulkWaitlist();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    translate: true,
  });

  const handleOnClick = async () => {
    const count = bulk.selectionSize;
    trackFormAction({
      verb: "Clicked",
      actionType: "REMOVE_FROM_WAITLIST",
      bulk: true,
      count,
      async: true,
    });

    if (
      !(await confirm({
        header: (
          <Text>{`Remove ${count} ${maybePluralize(
            count,
            "form"
          )} from ${maybePluralize(count, "waitlist")}?`}</Text>
        ),
        body: (
          <Text>
            <Glossary>{confirmationBodyText(count)}</Glossary>
          </Text>
        ),
        confirmButton: {
          label: confirmButtonLabel(count),
          colorScheme: "blue",
        },
      }))
    ) {
      trackFormAction({
        verb: "Canceled",
        actionType: "REMOVE_FROM_WAITLIST",
        bulk: true,
        count,
        async: true,
      });
      return;
    }

    trackFormAction({
      verb: "Confirmed",
      actionType: "REMOVE_FROM_WAITLIST",
      bulk: true,
      count,
      async: true,
    });
    await enqueueBulkWaitlist({ action: "REMOVE_FROM_WAITLIST" });
  };

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canRemoveFromWaitlists(
            selectedFormSchoolStatuses,
            selectedFormSchools
          )
        }
      >
        {menuLabel}
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
